import { Settings } from '@wfp/icons-react';
import { Button, Modal, Text } from '@wfp/react';
import React, { useCallback } from 'react';
import { connect } from 'react-redux';

import { ModalFooter, ReviewNav, ReviewsList } from './components';
import {
  ASSET_STATUSES,
  ASSET_STATUSES_URL,
  ASSETS,
  ASSETS_URL,
} from './constants';
import { useTechnicalReviewLogic, useTechnicalReviewState } from './hooks';
import { mapDispatchToProps, mapStateToProps } from './redux';
import { ITechnicalReviewProps } from './types';

export { ASSET_STATUSES_URL, ASSETS_URL, ASSETS, ASSET_STATUSES };

export const TechnicalReviewComponent: React.FC<
  ITechnicalReviewProps
> = props => {
  const state = useTechnicalReviewState();
  const logic = useTechnicalReviewLogic(props, state);

  const {
    isActive,
    activeGroup,
    toggleIconVisibility,
    editReviewsMode,
    handleOpen,
    handleClose,
    handleStartEditIcons,
    handleCancelEditIcons,
    setActiveGroupHandler,
  } = state;

  const {
    handleSubmitEditedIcons,
    handleReviewStatusToggle,
    handleIconVisibilityToggle,
    handleIconVisibilityToggleAll,
  } = logic;

  const renderModalContent = useCallback(() => {
    const assets = props.assets.data;
    const hasMultipleGroups = assets && assets?.groups?.length > 1;

    return (
      <>
        <header
          className={hasMultipleGroups ? 'has-navigation' : 'single-group'}
        >
          <div className="header-content">
            <div className="title-section">
              {editReviewsMode ? (
                <Text kind="bold" className="modal-title-left-aligned">
                  Add/Remove functions
                </Text>
              ) : (
                <Text kind="bold" className="modal-title-left-aligned">
                  Review by function
                </Text>
              )}
            </div>
            {hasMultipleGroups && (
              <div className="navigation-section">
                <ReviewNav
                  assetsData={assets}
                  activeGroup={activeGroup}
                  setActiveGroupHandler={setActiveGroupHandler}
                />
              </div>
            )}
          </div>
        </header>

        <div className="tr-modal-content">
          {assets ? (
            <ReviewsList
              assetsData={assets}
              activeGroup={activeGroup}
              editReviewsMode={editReviewsMode}
              toggleIconVisibility={toggleIconVisibility}
              handleIconVisibilityToggle={handleIconVisibilityToggle}
              props={props}
              isFetching={props[ASSET_STATUSES]?.isFetching}
              handleReviewStatusToggle={handleReviewStatusToggle}
            />
          ) : null}
        </div>
        {props.hasPermissionToMark &&
          props.sectionData?.data?.permissions?.can_change_reviewasset && (
            <footer>
              <ModalFooter
                editReviewsMode={editReviewsMode}
                handleSubmitEditedIcons={handleSubmitEditedIcons}
                handleCancelEditIcons={handleCancelEditIcons}
                handleIconVisibilityToggleAll={handleIconVisibilityToggleAll}
                handleStartEditIcons={handleStartEditIcons}
              />
            </footer>
          )}
      </>
    );
  }, [
    props,
    editReviewsMode,
    activeGroup,
    toggleIconVisibility,
    handleIconVisibilityToggle,
    handleReviewStatusToggle,
    setActiveGroupHandler,
    handleSubmitEditedIcons,
    handleCancelEditIcons,
    handleIconVisibilityToggleAll,
    handleStartEditIcons,
  ]);

  return (
    <>
      <Button
        onClick={handleOpen}
        kind="secondary"
        data-test-id="review-by-function-button"
        icon={<Settings height=".8em" />}
      >
        Review by function
      </Button>
      <Modal
        open={isActive}
        className={`technical-review-modal ${
          editReviewsMode ? 'edit-mode' : ''
        }`}
        passiveModal
        onRequestClose={handleClose}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export const TechnicalReview = connector(TechnicalReviewComponent);
